import { createSlice } from '@reduxjs/toolkit';
import {
    FENIX_FLOW_STATUS,
    FENIX_YMS_COPY,
    HISTORY_STATUS,
    YMS_EXPORT_STATUS,
    deriveStateFromAttempts,
    isValidExportStatus,
    normalizeAttempt,
} from 'utils/fenixYms';

const initialState = {
    byTransactionId: {},
    pendingTabActivations: {},
};

const createDefaultEntry = () => ({
    requestId: null,
    status: FENIX_FLOW_STATUS.IDLE,
    fenixSubmissionResult: null,
    exportButtonLocked: false,
    ymsUrl: null,
    ymsDetails: null,
    statusMessage: null,
    attempts: [],
    historyStatus: HISTORY_STATUS.IDLE,
    localPendingId: null,
});

const ensureEntry = (state, transactionId) => {
    if (!state.byTransactionId[transactionId]) {
        state.byTransactionId[transactionId] = createDefaultEntry();
    }
    return state.byTransactionId[transactionId];
};

let transientCounter = 0;
const nextTransientId = () => {
    transientCounter += 1;
    return `fenix-pending-${Date.now()}-${transientCounter}`;
};

const applyDerivedState = (entry) => {
    const derived = deriveStateFromAttempts(entry.attempts);
    entry.status = derived.status;
    entry.exportButtonLocked = derived.exportButtonLocked;
    entry.fenixSubmissionResult = derived.fenixSubmissionResult;
    entry.ymsUrl = derived.ymsUrl;
    entry.ymsDetails = derived.ymsDetails;
    entry.statusMessage = derived.statusMessage;
    if (derived.requestId) entry.requestId = derived.requestId;
};

const fenixYmsSlice = createSlice({
    name: 'fenixYms',
    initialState,
    reducers: {
        exportToYmsStart() {},

        fetchHistoryStart(state, { payload: { transactionId } }) {
            const entry = ensureEntry(state, transactionId);
            entry.historyStatus = HISTORY_STATUS.LOADING;
        },

        historyLoaded(state, { payload: { transactionId, attempts = [] } }) {
            const entry = ensureEntry(state, transactionId);
            entry.historyStatus = HISTORY_STATUS.LOADED;

            const incomingRequestIds = new Set(attempts.map((attempt) => attempt.requestId).filter(Boolean));

            const survivingLocal = (entry.attempts || []).filter((attempt) => !attempt.requestId || !incomingRequestIds.has(attempt.requestId));

            entry.attempts = [...attempts, ...survivingLocal];
            applyDerivedState(entry);
        },

        historyLoadFailed(state, { payload: { transactionId } }) {
            const entry = ensureEntry(state, transactionId);
            entry.historyStatus = HISTORY_STATUS.ERROR;
        },

        exportStarted(state, { payload: { transactionId } }) {
            const entry = ensureEntry(state, transactionId);
            const attempt = normalizeAttempt(
                { status: YMS_EXPORT_STATUS.PENDING, started: new Date().toISOString(), transient: true },
                entry.attempts.length
            );
            attempt.id = nextTransientId();
            entry.localPendingId = attempt.id;
            entry.attempts.push(attempt);
            applyDerivedState(entry);
        },

        exportInitPending(state, { payload: { transactionId, requestId } }) {
            const entry = ensureEntry(state, transactionId);
            entry.requestId = requestId ?? entry.requestId;
            const pending = entry.attempts.find((attempt) => attempt.id === entry.localPendingId);
            if (pending) {
                pending.requestId = requestId ?? null;
                if (requestId != null) pending.id = String(requestId);
                entry.localPendingId = pending.id;
            }
            applyDerivedState(entry);
        },

        exportInitFailed(state, { payload: { transactionId, message } }) {
            const entry = ensureEntry(state, transactionId);
            const pending = entry.attempts.find((attempt) => attempt.id === entry.localPendingId);
            if (pending) {
                pending.status = YMS_EXPORT_STATUS.ERROR;
                pending.message = message || FENIX_YMS_COPY.exportFailed;
                pending.finished = new Date().toISOString();
            } else {
                entry.attempts.push(
                    normalizeAttempt(
                        {
                            status: YMS_EXPORT_STATUS.ERROR,
                            message: message || FENIX_YMS_COPY.exportFailed,
                            started: new Date().toISOString(),
                            finished: new Date().toISOString(),
                            transient: true,
                        },
                        entry.attempts.length
                    )
                );
            }
            entry.localPendingId = null;
            applyDerivedState(entry);
        },

        discardPendingExport(state, { payload: { transactionId } = {} }) {
            if (transactionId == null) return;
            const entry = state.byTransactionId[transactionId];
            if (!entry) return;
            if (entry.localPendingId) {
                entry.attempts = entry.attempts.filter((attempt) => attempt.id !== entry.localPendingId);
            }
            entry.localPendingId = null;
            applyDerivedState(entry);
        },

        exportResponseReceived(state, { payload }) {
            const { transactionId, requestId, status, message, details, url, timestamp } = payload || {};
            if (transactionId == null) return;
            const entry = ensureEntry(state, transactionId);

            let attempt = requestId != null ? entry.attempts.find((item) => item.requestId === requestId) : null;
            if (!attempt && entry.localPendingId) {
                attempt = entry.attempts.find((item) => item.id === entry.localPendingId);
            }

            if (attempt) {
                if (requestId != null) {
                    attempt.requestId = requestId;
                    attempt.id = String(requestId);
                }
                attempt.status = isValidExportStatus(status) ? status : attempt.status;
                attempt.message = message ?? attempt.message;
                attempt.details = details ?? null;
                attempt.url = url ?? null;
                attempt.finished = timestamp ?? new Date().toISOString();
                attempt.transient = false;
            } else {
                entry.attempts.push(
                    normalizeAttempt(
                        {
                            requestId,
                            status,
                            message,
                            details,
                            url,
                            started: timestamp,
                            finished: timestamp,
                        },
                        entry.attempts.length
                    )
                );
            }

            entry.localPendingId = null;
            applyDerivedState(entry);
        },

        requestTabActivation(state, { payload: { transactionId } }) {
            state.pendingTabActivations[transactionId] = true;
        },

        clearTabActivation(state, { payload: { transactionId } = {} }) {
            if (transactionId == null) return;
            delete state.pendingTabActivations[transactionId];
        },

        removeTransaction(state, { payload: transactionId }) {
            delete state.byTransactionId[transactionId];
            delete state.pendingTabActivations[transactionId];
        },

        resetAll() {
            return initialState;
        },
    },
});

export const fenixYmsReducer = fenixYmsSlice.reducer;
export const fenixYmsActions = fenixYmsSlice.actions;
